import { useEffect, useMemo, useState } from 'react'
import { TaskActualWorkApproveUserDto } from '../../../../../../applications/ports/taskActualWorkApproveRepository'

export type FilterInfo = {
  hasDivisionFilter: boolean
  hasTeamFilter: boolean
  isFilterActive: boolean
}

export const createDefaultFilterInfo = (): FilterInfo => ({
  hasDivisionFilter: false,
  hasTeamFilter: false,
  isFilterActive: false,
})

interface UseFilterStateProps {
  allUsersWithStatus: TaskActualWorkApproveUserDto[]
  onFilterChange?: (filterInfo: FilterInfo) => void
  onFilteredUsersChange?: (users: TaskActualWorkApproveUserDto[]) => void
}

export const useFilterState = ({
  allUsersWithStatus,
  onFilterChange,
  onFilteredUsersChange,
}: UseFilterStateProps) => {
  const [selectedDivisionUuid, setSelectedDivisionUuid] = useState<
    string | undefined
  >()
  const [selectedTeamUuid, setSelectedTeamUuid] = useState<string | undefined>()
  const [showRequestingOnly, setShowRequestingOnly] = useState(false)

  const divisions = useMemo(() => {
    const divisionMap = new Map()
    allUsersWithStatus.forEach(user => {
      const div = user.projectMemberDetail.user.division
      if (div && !divisionMap.has(div.uuid)) {
        divisionMap.set(div.uuid, {
          uuid: div.uuid,
          displayName: div.displayName,
        })
      }
    })
    return Array.from(divisionMap.values())
  }, [allUsersWithStatus])

  const teams = useMemo(() => {
    const teamMap = new Map()
    allUsersWithStatus.forEach(user => {
      const team = user.projectMemberDetail.team
      if (team && !teamMap.has(team.uuid)) {
        teamMap.set(team.uuid, {
          uuid: team.uuid,
          displayName: team.displayName,
        })
      }
    })
    return Array.from(teamMap.values())
  }, [allUsersWithStatus])

  const filteredUsers = useMemo(() => {
    let result = allUsersWithStatus

    if (selectedDivisionUuid) {
      result = result.filter(
        u => u.projectMemberDetail.user.division?.uuid === selectedDivisionUuid
      )
    }

    if (selectedTeamUuid) {
      result = result.filter(
        u => u.projectMemberDetail.team?.uuid === selectedTeamUuid
      )
    }

    if (showRequestingOnly) {
      result = result.filter(u => u.requestingCount > 0)
    }

    return result.sort((a, b) =>
      (a.projectMemberDetail.user.name || '').localeCompare(
        b.projectMemberDetail.user.name || ''
      )
    )
  }, [
    allUsersWithStatus,
    selectedDivisionUuid,
    selectedTeamUuid,
    showRequestingOnly,
  ])

  useEffect(() => {
    if (onFilterChange) {
      const isFilterActive = !!selectedDivisionUuid || !!selectedTeamUuid
      onFilterChange({
        hasDivisionFilter: !!selectedDivisionUuid,
        hasTeamFilter: !!selectedTeamUuid,
        isFilterActive,
      })
    }
  }, [selectedDivisionUuid, selectedTeamUuid, onFilterChange])

  // フィルタリング済みユーザーリストを親に通知
  useEffect(() => {
    if (onFilteredUsersChange) {
      onFilteredUsersChange(filteredUsers)
    }
  }, [filteredUsers, onFilteredUsersChange])

  return {
    selectedDivisionUuid,
    selectedTeamUuid,
    setSelectedDivisionUuid,
    setSelectedTeamUuid,
    showRequestingOnly,
    setShowRequestingOnly,
    divisions,
    teams,
    filteredUsers,
  }
}
